import { useState, FormEvent } from 'react';
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Link, Stack, TextField, Typography } from '@mui/material';
import { authService } from '../../shared/services/auth.service';
import { useAuth } from '../../shared/hooks/useAuth';
import { useApiError } from '../../shared/errors/useApiError';
import { useNotification } from '../../shared/notifications/NotificationProvider';
import { SplitAuthLayout, MarketingBenefits } from '../../shared/components/layout/SplitAuthLayout';
import { validators } from '../../shared/validators/auth-validators';
import { PasswordField } from '../../shared/components/ui/PasswordField';
// Google OAuth temporarily disabled until OAUTH_GOOGLE_* env vars are
// wired up on test + prod (Jenkins secret files). Re-enable this import
// and the <SocialAuthButtons /> below once the backend is configured.
// import { SocialAuthButtons } from '../../shared/components/ui/SocialAuthButtons';

type FieldKey =
  | 'firstName'
  | 'lastName'
  | 'username'
  | 'email'
  | 'password'
  | 'confirmPassword';

export function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { notify } = useNotification();
  const { setSession } = useAuth();
  const { translate } = useApiError();
  const [loading, setLoading] = useState(false);

  // Honour an explicit ?next= only after verification finishes.
  const nextParam = searchParams.get('next');
  const safeNext = nextParam && nextParam.startsWith('/') ? nextParam : '/dashboard';

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({});

  const set = (key: FieldKey, value: string) => {
    setForm((prev) => {
      const nextForm = { ...prev, [key]: value };
      // Live mismatch feedback: re-evaluate the confirm field as the user
      // edits either password input so they spot a typo immediately instead
      // of only at submit time.
      if (key === 'password' || key === 'confirmPassword') {
        setErrors((prevErr) => {
          const updated = { ...prevErr, [key]: undefined };
          const pw = nextForm.password;
          const cpw = nextForm.confirmPassword;
          if (cpw.length > 0 && pw !== cpw) {
            updated.confirmPassword = 'auth.passwordMismatch';
          } else {
            updated.confirmPassword = undefined;
          }
          return updated;
        });
      } else {
        setErrors((prevErr) => ({ ...prevErr, [key]: undefined }));
      }
      return nextForm;
    });
  };

  const validate = (): boolean => {
    // Simplified signup: only email + password are required. First name and
    // last name are optional and can be added later from /account/personal.
    const next: Partial<Record<FieldKey, string>> = {};
    const emailKey = validators.email(form.email);
    if (emailKey) next.email = emailKey;
    const passwordKey = validators.password(form.password);
    if (passwordKey) next.password = passwordKey;
    if (form.password !== form.confirmPassword) {
      next.confirmPassword = 'auth.passwordMismatch';
    }
    // Username is optional — only check the format when the user typed
    // something. Mirrors the backend regex in AuthDtos so the user sees the
    // problem before the request hits the API (avoids the cryptic 400
    // "request payload is invalid").
    const handle = form.username.trim();
    if (handle && !/^[A-Za-z][A-Za-z0-9_]{2,29}$/.test(handle)) {
      next.username = 'auth.usernameInvalid';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const errText = (k: FieldKey) => (errors[k] ? t(errors[k] as string) : undefined);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const result = await authService.register({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        // Omit when blank — the backend generates a neutral username and
        // rejects an empty-string value.
        username: form.username.trim() || undefined,
        email: form.email.trim(),
        password: form.password,
      });
      setSession(result);
      notify.success(t('auth.registerSuccess'));
      // Always land on verification first — the server has already mailed the
      // email code. The phone step is gated until email is verified.
      navigate(`/verify-email?next=${encodeURIComponent(safeNext)}`, { replace: true });
    } catch (err) {
      notify.error(translate(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <SplitAuthLayout
      variant="customer"
      marketing={
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1.15, mb: 2 }}>
            {t('auth.marketing.customerTitle')}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ color: 'inherit', opacity: 0.92, fontSize: '1.0625rem' }}
          >
            {t('auth.marketing.customerSubtitle')}
          </Typography>
          <MarketingBenefits
            items={[
              t('auth.marketing.customerBenefit1'),
              t('auth.marketing.customerBenefit2'),
              t('auth.marketing.customerBenefit3'),
            ]}
          />
        </Box>
      }
    >
      <Typography variant="h3" sx={{ mb: 1 }}>
        {t('auth.welcome')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 4 }}>
        {t('auth.registerTitle')}
      </Typography>

      {/* Google OAuth disabled until backend env vars are set (see import above). */}
      {/* <SocialAuthButtons variant="customer" /> */}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={`${t('auth.firstName')} (${t('auth.optionalLabel')})`}
              value={form.firstName}
              onChange={(e) => set('firstName', e.target.value)}
              fullWidth
              error={!!errors.firstName}
              helperText={errText('firstName')}
            />
            <TextField
              label={`${t('auth.lastName')} (${t('auth.optionalLabel')})`}
              value={form.lastName}
              onChange={(e) => set('lastName', e.target.value)}
              fullWidth
              error={!!errors.lastName}
              helperText={errText('lastName')}
            />
          </Stack>

          <TextField
            label={t('auth.email')}
            type="email"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
            required
            fullWidth
            autoComplete="email"
            placeholder="vous@exemple.com"
            error={!!errors.email}
            helperText={errText('email')}
          />

          <TextField
            label={`${t('auth.username')} (${t('auth.optionalLabel')})`}
            value={form.username}
            onChange={(e) => set('username', e.target.value)}
            fullWidth
            autoComplete="username"
            error={!!errors.username}
            helperText={errText('username') || t('auth.usernameHint')}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <PasswordField
              label={t('auth.password')}
              value={form.password}
              onChange={(e) => set('password', e.target.value)}
              required
              fullWidth
              autoComplete="new-password"
              error={!!errors.password}
              helperText={errText('password')}
            />
            <PasswordField
              label={t('auth.confirmPassword')}
              value={form.confirmPassword}
              onChange={(e) => set('confirmPassword', e.target.value)}
              required
              fullWidth
              autoComplete="new-password"
              error={!!errors.confirmPassword}
              color={
                !errors.confirmPassword &&
                form.confirmPassword.length > 0 &&
                form.password === form.confirmPassword
                  ? 'success'
                  : undefined
              }
              focused={
                !errors.confirmPassword &&
                form.confirmPassword.length > 0 &&
                form.password === form.confirmPassword
                  ? true
                  : undefined
              }
              helperText={
                errText('confirmPassword') ||
                (form.confirmPassword.length > 0 && form.password === form.confirmPassword
                  ? t('auth.passwordMatch')
                  : undefined)
              }
            />
          </Stack>

          <Typography variant="caption" color="text.secondary">
            {t('auth.passwordHint')}
          </Typography>

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loading}
            fullWidth
            sx={{ py: 1.5, fontSize: '1rem' }}
          >
            {loading ? t('common.loading') : t('auth.register')}
          </Button>
        </Stack>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
        {t('auth.alreadyHaveAccount')}{' '}
        <Link component={RouterLink} to="/login" sx={{ color: 'primary.main', fontWeight: 600 }}>
          {t('auth.login')}
        </Link>
      </Typography>

      <Box
        sx={{
          mt: 3,
          p: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t('auth.areYouAPro')}{' '}
          <Link component={RouterLink} to="/pro" sx={{ color: 'primary.main', fontWeight: 600 }}>
            {t('nav.createProAccount')}
          </Link>
        </Typography>
      </Box>
    </SplitAuthLayout>
  );
}
